import { Plugin, PluginKey } from "prosemirror-state"

import fetchImageAndConvertToBlob from "@/helpers/fetchImageAndConvertToBlob"
import { InsertFilePreview, uploadAttachments } from "@/utils/helpers"

import { TypeConfig, UploadAttachmentFn } from "../File"

export const dropAttachmentPlugin = (
  upload: UploadAttachmentFn,
  config: TypeConfig | null,
  insertFilePreview?: InsertFilePreview
) =>
  new Plugin({
    key: new PluginKey("dropAttachmentPlugin"),
    props: {
      handlePaste(view, event, slice) {
        const items = Array.from(event.clipboardData?.items || [])
        const files = Array.from(event.clipboardData?.files || [])
        const hasText = items.some(item => item.type === "text/plain")
        const hasHtml = items.some(item => item.type === "text/html")
        const html = event.clipboardData?.getData("text/html") as string
        const hasImageInHtml = !!html && /<img[^>]+src=["']([^"']+)["'][^>]*>/i.test(html)
        const { state, dispatch } = view

        const shouldHandleAsAttachment = files.length > 0 || hasImageInHtml
        if (!shouldHandleAsAttachment) {
          // Allow ProseMirror to handle normal paste (keeps Link extension paste behavior).
          return false
        }

        const tr = state.tr.replaceSelection(slice)
        dispatch(tr)

        // Prevent the default paste action
        event.preventDefault()

        const handleImageUpload = async ({
          imageUrl,
          position,
          type
        }: {
          imageUrl
          position?: number
          type?: string
        }) => {
          try {
            const blob = await fetchImageAndConvertToBlob(imageUrl)
            const file = new File([blob], "image.jpg")
            uploadAttachments({
              upload,
              files: [file],
              view,
              config,
              position,
              type,
              insertFilePreview
            })
          } catch (error) {
            console.error("Error fetching image:", error)
          }
        }

        if (hasText && hasHtml) {
          view.state.doc.content.descendants((fragment, position) => {
            fragment.content.forEach(node => {
              if (node.type.name === "attachment") {
                handleImageUpload({
                  imageUrl: node.attrs.src,
                  position: position + node.nodeSize,
                  type: "PASTE"
                })
              }
            })
            return false
          })
          return true
        }
        if (files.length > 0) {
          uploadAttachments({
            upload,
            files,
            view,
            config,
            insertFilePreview
          })
          return true
        }
        if (hasHtml) {
          items.forEach(item => {
            if (item.type === "text/html") {
              if (!html) return
              const imageUrls = html.match(/src="([^"]*)"/g)
              if (!imageUrls) return
              const src = imageUrls?.[0].replace('src="', "").replace('"', "")
              if (src) {
                handleImageUpload({ imageUrl: src })
              }
            }
          })
        }

        return true
      },
      handleDrop(view, event) {
        const files = Array.from(event.dataTransfer?.files || [])
        if (files.length === 0) return false
        const coordinates = view.posAtCoords({
          left: event.clientX,
          top: event.clientY
        })
        if (!coordinates) return false
        event.preventDefault()
        uploadAttachments({
          upload,
          files,
          view,
          config,
          position: coordinates.pos,
          insertFilePreview
        })
        return true
      }
    }
  })
